import { useEffect } from "react";
import { create } from "zustand";

interface BottomCtaBarState {
  /** 지금 화면에 떠 있는 하단 고정 CTA 바의 개수. */
  count: number;
  increment: () => void;
  decrement: () => void;
}

const useBottomCtaBarStore = create<BottomCtaBarState>((set) => ({
  count: 0,
  increment: () => set((s) => ({ count: s.count + 1 })),
  decrement: () => set((s) => ({ count: Math.max(0, s.count - 1) })),
}));

/**
 * 모바일 하단 고정 CTA 바(제품 페이지의 `ScrollHideButtonByFooter`)가 떠 있는지.
 *
 * 화면에 떠 있는 요소(신규 가입 쿠폰 배지 등)가 **구매 흐름을 방해하지 않도록** 물러설 때
 * 쓴다. DOM 을 뒤지지 않고 컴포넌트가 스스로 등록하게 한 이유는, 바가 스크롤 위치에 따라
 * 붙었다 떨어졌다 해서 한 번 조회한 결과가 곧 낡기 때문이다.
 *
 * ⚠️ **"몇 px 위로 비켜설지" 를 상수로 박으려 하지 말 것.** 바 높이는 사용처마다 다르다 —
 * 브라우저 실측(375px 뷰포트) 기준 `TactsuitPageTemplate` **92px** /
 * `BuyTactsuitDetailPageTemplate` **180px**
 * (종전엔 `BuyTactsuitPageTemplate` 약 122px 도 함께 적었는데 그 템플릿은 #5705 에서
 *  지웠다 — 소비처가 0이 됐다. 없는 컴포넌트를 비교 기준으로 두지 않는다.)
 * (배송일·재고 행 + Add to Bag + 게스트/계정 결제, KR 은 네이버페이가 한 단 더).
 * 초안이 "약 92px" 를 근거로 `bottom-28`(112px)을 박았다가, 정작 구매가 일어나는
 * `/shop/buy-tactsuit/*` PDP 에서 배지가 `Add to Bag` 위에 얹혔다(배지 z-50 > 바 z-30,
 * `elementFromPoint` 로 실제 탭 가로챔 확인). 그래서 지금은 비켜서는 대신 **숨긴다.**
 *
 * ⚠️ 바는 `laptop:hidden` 이라 **데스크톱에서도 마운트는 되어 있다**(CSS 로만 숨김).
 * 따라서 이 값이 true 여도 데스크톱에서는 반영하면 안 된다 — 소비하는 쪽에서 모바일
 * 한정으로만 쓸 것.
 */
export const useIsBottomCtaBarVisible = () =>
  useBottomCtaBarStore((s) => s.count > 0);

/** 하단 고정 CTA 바가 실제로 그려지는 동안 등록한다. */
export function useRegisterBottomCtaBar(active: boolean) {
  const increment = useBottomCtaBarStore((s) => s.increment);
  const decrement = useBottomCtaBarStore((s) => s.decrement);

  useEffect(() => {
    if (!active) return;
    increment();
    return decrement;
  }, [active, increment, decrement]);
}
